import { Component, effect, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { InfoService } from "@core-services/info.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { ModalService } from "@core-services/modal.service";
import { MarginHealthIndicator, PositionStatus } from "@gql/offer";
import { LARGE_DEVICES_WIDTH_THRESHOLD } from "@helpers/constants";
import { activateDebug } from "@helpers/debug";
import { matchesMediaQuery } from "@helpers/utils";
import { SavingsService } from "@main-services/savings.service";
import { SegmentService } from "@main-services/segment.service";
import { UserService } from "@main-services/user.service";
import { FiatWallet, UserVerificationState } from "@models";
import { Feature } from "@models/feature-list";
import { PageTracker } from "@models/interfaces/page-tracker";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { CashPlusWidgetFacade } from "@modules/cashplus/ui/cashplus-widget/cashplus-widget.facade";
import { KycService } from "@modules/kyc/services/kyc.service";
import { LaunchpadTrackingFacade } from "@modules/launchpad/domain/+state/launchpad-tracking.facade";
import { MtSuccessFacade } from "@modules/margin-trading/components/mt-success/mt-success.facade";
import { MarginTradingFacade } from "@modules/margin-trading/services/margin-trading.facade";
import { MarginTradingRemote } from "@modules/margin-trading/services/margin-trading.remote";
import { PromptsFacade } from "@modules/messaging-prompts/domain/+state/prompts.facade";
import { PromptsModel } from "@modules/messaging-prompts/domain/entities/prompts.model";
import { BestRewardsWeeklyFacade } from "@modules/new_account/features/my-best/domain/+state/best-rewards-weekly.facade";
import {
	BestWidgetModel,
	BestWidgetWeeklyFacade
} from "@modules/new_account/features/my-best/domain/+state/best-widget-weekly.facade";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { AntiPhishingCodeModalService } from "@modules/shared/modals/anti-phishing-code-modal/services/anti-phishing-code-modal.service";
import { StakingBannerFacade } from "@modules/staking/domain/+state/staking-banner.facade";
import { TaxesFacade } from "@modules/taxes/domain/+state/taxes.facade";
import { WEB3_MERGE_PHASE, Web3Facade } from "@modules/web3/+state/web3.facade";
import {
	COOL_OFF_TRADE_STATUSES,
	CoolOffWidgetModel,
	QUESTIONNAIRE_STATUSES
} from "@modules/uk-finprom/components/cool-off-widget/entities/cool-off-widget.model";
import { CoolOffWidgetRemote } from "@modules/uk-finprom/components/cool-off-widget/infrastructure/cool-off-widget.remote";
import { CoolOffNotificationModalComponent } from "@modules/uk-finprom/components/cool-off-notification-modal/cool-off-notification-modal.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import {
	MarginPositionFragment,
	PortfolioMarginPositionsPageFragment
} from "app/api/graphql/bff/generated/fragments.service.graphql-gen";
import { MarginPositionSort, SortOrder } from "app/api/graphql/bff/generated/types.graphql-gen";
import { combineLatest, EMPTY, Observable } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { NotFoundModalComponent } from "../../main/components/not-found-modal/not-found-modal.component";
import { DepositsBannerFacade } from "./deposits-banner/deposits-banner.facade";
import { AppropriatenessModalComponent } from "@modules/main/components/stocks/appropriateness-modal/appropriateness-modal.component";
import { StartCustomerSegmentationModalComponent } from "@modules/uk-finprom/components/customer-segmentation/components/start-customer-segmentation-modal/start-customer-segmentation-modal.component";
import { RudderstackService } from "@main-services/rudderstack.service";
import {
	ITinVerificationWidgetStatusResponse,
	TinVerificationState
} from "@modules/taxes/domain/infrastructure/taxes.remote";
import { IframeV4EntryHelperService } from "@modules/iframeV4/services/iframeV4.entryhelper.service";
import { TradeSavingsComponent } from "@modules/trade/modals/trade-savings-modal/trade-savings-modal.component";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { FiatWithdrawalModalComponent } from "@modules/main/components/step-summary/fiat-transactions/fiat-withdraw-modal/fiat-withdraw-modal.component";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { FiatTransaction, SendTransaction } from "@models/transaction";

@UntilDestroy()
@Component({
	templateUrl: "./home.component.html",
	styleUrls: ["./home.component.scss"],
	providers: [BestRewardsWeeklyFacade, BestWidgetWeeklyFacade, DepositsBannerFacade, LaunchpadTrackingFacade],
	standalone: false
})
export class HomeComponent extends PageTracker implements OnInit {
	private readonly kycService = inject(KycService);
	private readonly apcModalService = inject(AntiPhishingCodeModalService);
	private readonly promptsFacade = inject(PromptsFacade);
	private readonly web3Facade = inject(Web3Facade);
	private readonly mtSuccessFacade = inject(MtSuccessFacade);
	private readonly marginTradingFacade = inject(MarginTradingFacade);
	private readonly taxFacade = inject(TaxesFacade);
	private readonly iframeV4EntryHelperService = inject(IframeV4EntryHelperService);
	protected trackingPageName: string = "Home Screen Viewed";
	public featureList: typeof Feature = Feature;
	public showTopMovers: boolean = false;
	public web3MergePhase = WEB3_MERGE_PHASE;
	public mergePhase$ = this.web3Facade.getMergePhase();
	public shouldShowWeb3MergePrompt = false;
	public showCoolOffWidget: boolean = false;

	public showStories$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.UK_FINPROM_BITPANDA_STORIES);
	public isMarginTradingEnabled: boolean = false;
	public showKycBanner: Observable<boolean> = this.kycService.showKycDashboardBanner();
	private readonly showStakingBanner$: Observable<boolean> = this.stakingBannerFacade.getCurrentlyStaking$();
	public isPromptWeb3 = false;
	public tinVerificationStatus$ = this.taxFacade.getWidgetTinVerificationStatus$();

	public readonly isUserVerified$: Observable<boolean> = this.userService.verificationState$.pipe(
		map((state) => state === UserVerificationState.VERIFIED)
	);

	public bestWidgetModel$: Observable<BestWidgetModel> = this.bestWidgetWeeklyFacade.getBestWidgetViewModel$();
	public coolOffWidgetModel$: Observable<CoolOffWidgetModel> = combineLatest([
		this.coolOffWidgetRemote.getPreTradeData$(),
		this.isUserVerified$
	]).pipe(
		map(([data, isUserVerified]) => {
			if (!isUserVerified) {
				return data;
			}

			const { status, client_categorization_status, appropriateness_test_status } = data;

			if (status === COOL_OFF_TRADE_STATUSES.COOLING_OFF || status === COOL_OFF_TRADE_STATUSES.BLOCKED) {
				this.showCoolOffWidget = true;
			}

			if (status !== COOL_OFF_TRADE_STATUSES.BLOCKED) {
				if (client_categorization_status === QUESTIONNAIRE_STATUSES.PENDING) {
					this.modalService
						.create(StartCustomerSegmentationModalComponent, null, {
							preventBackdropDismiss: true,
							preventEscDismiss: true
						})
						.present();
				} else if (appropriateness_test_status === QUESTIONNAIRE_STATUSES.PENDING) {
					this.modalService
						.create(AppropriatenessModalComponent, null, {
							preventBackdropDismiss: true,
							preventEscDismiss: true
						})
						.present();
				} else if (status === COOL_OFF_TRADE_STATUSES.PENDING_CHECK) {
					this.modalService
						.create(CoolOffNotificationModalComponent, { data: data }, { preventEscDismiss: true })
						.present();
				}
			}

			return data;
		})
	);
	public showBEST: boolean = false;

	public readonly viewModel$: Observable<HomeComponentViewModel> = combineLatest([
		this.isUserVerified$,
		this.depositsBannerFacade.getDepositsBannerVisibility$(),
		this.showStakingBanner$,
		this.savingsService.hasAnyActivePendingOrPausedPlan,
		this.cashplusWidgetFacade.showCashplusWidget$()
	]).pipe(
		map(
			([
				isUserVerified,
				shouldShowDepositsBanner,
				showStakingBanner,
				hasAnyActivePendingOrPausedPlan,
				showCashPlusWidget
			]) => ({
				showPortfolioWidget: !shouldShowDepositsBanner && isUserVerified,
				showActivityWidget: isUserVerified,
				showSavingsWidget: isUserVerified && hasAnyActivePendingOrPausedPlan,
				showBestWidget: isUserVerified,
				showStakingBanner,
				showCashPlusWidget
			})
		)
	);

	public viewModelFF$: Observable<HomeComponentViewModelFF> = combineLatest({
		taxesBannerVM: this.taxesFacade.getBannerHomeVM$,
		promptBannerVM: this.promptsFacade.promptsData$,
		apcPromptEnabled: this.promptsFacade.apcPromptEnabled$,
		mergePhase: this.web3Facade.getMergePhase(),
		tinVerificationStatus: this.taxFacade.getWidgetTinVerificationStatus$()
	}).pipe(
		map(({ taxesBannerVM, promptBannerVM, mergePhase, tinVerificationStatus }) => {
			const tinVerificationStatusSuccess = tinVerificationStatus as ITinVerificationWidgetStatusResponse;
			let promptsData = promptBannerVM.promptsData;
			let showPromptBanner = promptBannerVM.showBanner;
			this.isPromptWeb3 =
				promptsData[0]?.event.name === "web3-token-pre-merge" ||
				promptsData[0]?.event.name === "web3-token-post-merge";
			this.shouldShowWeb3MergePrompt =
				mergePhase.phase === this.web3MergePhase.PreMerge || mergePhase.phase === this.web3MergePhase.PostMerge;
			if (this.apcModalService.currentAntiPhishingCode) {
				promptsData = promptsData.filter((prompt) => prompt.event.name !== "apc-prompt");
				showPromptBanner = promptsData.length > 0;
			}

			const _tinVerificationStatus =
				tinVerificationStatusSuccess?.trading_state !== TinVerificationState.InReview
					? tinVerificationStatus
					: null;

			return {
				showTaxBanner: taxesBannerVM.showBanner,
				showPromptBanner,
				promptsData,
				isSelfCertificationStep: taxesBannerVM.isFirstStep,
				isUpdateInProgress: taxesBannerVM.isUpdateInProgress,
				tinVerificationStatus: _tinVerificationStatus
			};
		})
	);

	public marginPositions: MarginPositionFragment[] = [];

	public constructor(
		private readonly portfolioFacade: PortfolioFacade,
		private readonly modalService: ModalService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly infoService: InfoService,
		private readonly router: Router,
		private readonly launchDarklyService: LaunchDarklyService,
		protected readonly segmentService: SegmentService,
		protected readonly rudderstackService: RudderstackService,
		private readonly bestWidgetWeeklyFacade: BestWidgetWeeklyFacade,
		private readonly depositsBannerFacade: DepositsBannerFacade,
		private readonly savingsService: SavingsService,
		private readonly stakingBannerFacade: StakingBannerFacade,
		private readonly cashplusWidgetFacade: CashPlusWidgetFacade,
		private readonly cashPlusFacade: CashPlusFacade,
		private readonly taxesFacade: TaxesFacade,
		private readonly coolOffWidgetRemote: CoolOffWidgetRemote,
		private readonly marginTradingRemote: MarginTradingRemote,
		private readonly sendEntryPoint: SendEntryPointService,
		private readonly fiatWalletService: FiatWalletService
	) {
		super(segmentService, rudderstackService);
		this.route.params.forEach((params) => {
			if (params.hasOwnProperty("failure")) {
				if (params["failure"] === "notfound") {
					this.modalService.create(NotFoundModalComponent).present();
				} else if (
					params["failure"] === "makepandasgreatagain" &&
					this.userService.user.getEmail().endsWith("@bitpanda.com")
				) {
					activateDebug();
					this.infoService.setSuccess("debug activated");
				}
				this.router.navigateByUrl("/home");
			}
		});
		effect(() => {
			const vm = this.mtSuccessFacade.getViewModel$();
			if (!vm.acceptedOffer || vm.acceptedOffer.positionStatus !== PositionStatus.Close) {
				this.updateMarginPositions();
			} else {
				this.marginPositions = undefined;
			}
		});
	}

	public ngOnInit(): void {
		this.handleSavingsDeepLink();
		this.handleSendDeepLink();
		this.handleWithdrawDeepLink();
		this.cashPlusFacade.fetchCombinedCashPlusState();
		this.portfolioFacade.fetchPortfolioOverviewAndPerformance();
		this.promptsFacade.fetchPromptsData();

		// We don't need to fetch this for unverified users
		if (this.userService.user.isVerified()) {
			this.stakingBannerFacade.fetchBannerData();
		}
	}

	public seeAllPositions(): void {
		this.router.navigateByUrl("/portfolio?type=leveraged_token");
	}

	public isDesktopView(): boolean {
		return matchesMediaQuery(LARGE_DEVICES_WIDTH_THRESHOLD);
	}

	public isWeb3PromptHidden(): boolean {
		if (!this.isPromptWeb3 || !this.shouldShowWeb3MergePrompt) {
			return true;
		}
		const lastClickStorred = new Date(localStorage.getItem("web3DoItLater"));
		const threeDaysAfterClick = lastClickStorred
			? new Date(lastClickStorred).getTime() + 3 * 24 * 60 * 60 * 1000
			: null;
		return threeDaysAfterClick && new Date().getTime() < threeDaysAfterClick;
	}

	private updateMarginPositions(): void {
		this.marginTradingFacade
			.marginTradingEnabled$()
			.pipe(
				take(1),
				switchMap((isMarginTradingEnabled: boolean) => {
					this.isMarginTradingEnabled = isMarginTradingEnabled;
					return isMarginTradingEnabled
						? this.marginTradingRemote.getMarginPositionsFetch({
								marginPositionsInput: {
									filter: {
										healthIndicators: [MarginHealthIndicator.Low]
									},
									sortBy: MarginPositionSort.Performance,
									sortOrder: SortOrder.Asc
								},
								pageSize: 3
							})
						: EMPTY;
				})
			)
			.subscribe(
				(positions: PortfolioMarginPositionsPageFragment) =>
					(this.marginPositions = positions.edges?.map(({ node }) => node)) ?? []
			);
	}

	handleTinVerificationCTA(): void {
		this.iframeV4EntryHelperService.openModal("tin");
	}

	private handleSavingsDeepLink(): void {
		this.route.queryParams.pipe(take(1)).subscribe((queryParams) => {
			if (queryParams["savings"] !== "true") {
				return;
			}

			this.modalService.create(TradeSavingsComponent).present();

			this.router.navigate([], {
				queryParams: { savings: null },
				queryParamsHandling: "merge",
				replaceUrl: true
			});
		});
	}

	private handleSendDeepLink(): void {
		this.route.queryParams.pipe(take(1)).subscribe((queryParams) => {
			if (queryParams["send"] !== "true") {
				return;
			}

			this.sendEntryPoint.openCryptoSendModal({ transaction: new SendTransaction() });

			this.router.navigate([], {
				queryParams: { send: null },
				queryParamsHandling: "merge",
				replaceUrl: true
			});
		});
	}

	private handleWithdrawDeepLink(): void {
		this.route.queryParams.pipe(take(1)).subscribe((queryParams) => {
			if (queryParams["withdraw"] !== "true") {
				return;
			}

			const fiatWalletPid = queryParams["fiat_pid"];
			if (fiatWalletPid) {
				this.fiatWalletService
					.getList()
					.pipe(
						map(() => this.fiatWalletService.list.find((item) => item.getFiatId() === fiatWalletPid)),
						filter((wallet) => !!wallet),
						take(1)
					)
					.subscribe((wallet) => this.openWithdrawModal(wallet));
			} else {
				this.openWithdrawModal(null);
			}

			this.router.navigate([], {
				queryParams: { withdraw: null, fiat_pid: null },
				queryParamsHandling: "merge",
				replaceUrl: true
			});
		});
	}

	private openWithdrawModal(wallet: FiatWallet | null): void {
		const transaction = new FiatTransaction();
		transaction.setDirectFiatTransaction(true);
		transaction.setType("withdrawal");

		if (wallet) {
			transaction.setFiatWalletId(wallet.getId());
			transaction["preselected"] = wallet;
		}

		this.modalService.showModal(FiatWithdrawalModalComponent, { transaction });
	}
}

interface HomeComponentViewModel {
	showPortfolioWidget: boolean;
	showActivityWidget: boolean;
	showSavingsWidget: boolean;
	showBestWidget: boolean;
	showStakingBanner: boolean;
	showCashPlusWidget: boolean;
}

interface HomeComponentViewModelFF {
	showTaxBanner: boolean;
	showPromptBanner: boolean;
	isSelfCertificationStep: boolean;
	isUpdateInProgress: boolean;
	promptsData: PromptsModel[];
	tinVerificationStatus: Partial<ITinVerificationWidgetStatusResponse>;
}
